import { useState } from "react";
import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { Stack, Button, Typography } from "@mui/material";
import { Navigate, Route, Routes, useParams } from "react-router";
import { FullScreenDialog } from "../../../shared/FullScreenDialog";
import { PageSpinner } from "../../../shared/Page";
import { useGetTemplateFormSettingsById } from "../../../api/templateFormSettings";
import { TemplateFormDialogContent } from "./TemplateFormDialogContent";
import { useTemplateFormSectionsState } from "./TemplateFormProvider";
import { TemplateFormConfirmDialog } from "./TemplateFormConfirmDialog";
import { formTypeToTextAndStyle } from "./TemplateFormCard";
import { DIALOG_FOOTER_HEIGHT, DIALOG_HEADER_HEIGHT } from "./utils";

export const TemplateFormDialog: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const params = useParams<{ templateFormId: string | undefined }>();
  if (!params.templateFormId) throw new Error("params.templateFormId is required");

  const { data, isLoading } = useGetTemplateFormSettingsById(params.templateFormId);
  const { pendingChanges, clearPendingChanges } = useTemplateFormSectionsState();

  const [isFormConfirmDialogOpen, setIsFormConfirmDialogOpen] = useState(false);

  return (
    <FullScreenDialog overflowY="auto">
      {(() => {
        if (!data) return <PageSpinner />;

        const { labelKey } = formTypeToTextAndStyle[data.formType];
        return (
          <Stack sx={{ display: "flex", flexGrow: 1 }}>
            <Stack
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                height: DIALOG_HEADER_HEIGHT,
                justifyContent: "center",
                px: 4,
              }}
            >
              <Typography variant="h2" sx={{ lineHeight: 1.2, fontWeight: "600" }}>
                {t(labelKey)}
              </Typography>
            </Stack>
            <Routes>
              <Route path=":sectionId/*" element={<TemplateFormDialogContent formSettings={data} />} />
              <Route index element={<Navigate to={data.sections[0].id} replace />} />
            </Routes>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                px: 4,
                borderTop: 1,
                borderColor: "divider",
                height: DIALOG_FOOTER_HEIGHT,
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button
                disabled={isLoading || pendingChanges.length === 0}
                size="medium"
                sx={{ px: 3 }}
                onClick={() => setIsFormConfirmDialogOpen(true)}
              >
                {t("Template.Dialog.UpdateButton")}
              </Button>
              <Button
                size="medium"
                sx={{ px: 3 }}
                variant="outlined"
                onClick={() => {
                  clearPendingChanges();
                  onClose();
                }}
              >
                {t("Template.Dialog.CloseButton")}
              </Button>
            </Stack>
            {isFormConfirmDialogOpen && (
              <TemplateFormConfirmDialog
                id={params.templateFormId}
                pendingChanges={pendingChanges}
                onSaved={() => {
                  setIsFormConfirmDialogOpen(false);
                  clearPendingChanges();
                }}
                onCancel={() => setIsFormConfirmDialogOpen(false)}
              />
            )}
          </Stack>
        );
      })()}
    </FullScreenDialog>
  );
};
